/**
 * Market Data Calculations Utility
 *
 * This file contains all calculation functions for property market data analysis
 * including Price per m², Cash Flow, and Return on Equity (EK-Rendite).
 */

// Normalisiert Google-API Bundeslandnamen (offizielle Langformen & englische Namen → interne Keys)
const STATE_NAME_ALIASES = {
  "Freistaat Bayern": "Bayern",
  "Freistaat Sachsen": "Sachsen",
  "Freistaat Thüringen": "Thüringen",
  "Freie und Hansestadt Hamburg": "Hamburg",
  "Freie Hansestadt Bremen": "Bremen",
  "Land Brandenburg": "Brandenburg",
  "Land Sachsen-Anhalt": "Sachsen-Anhalt",
  "North Rhine-Westphalia": "Nordrhein-Westfalen",
  Bavaria: "Bayern",
  Saxony: "Sachsen",
  Thuringia: "Thüringen",
  "Lower Saxony": "Niedersachsen",
  "Rhineland-Palatinate": "Rheinland-Pfalz",
  "Saxony-Anhalt": "Sachsen-Anhalt",
  Hesse: "Hessen",
  "Mecklenburg-Western Pomerania": "Mecklenburg-Vorpommern",
};

const GRUNDERWERBSSTEUER_BY_STATE = {
  Bayern: 0.035,
  Hamburg: 0.045,
  "Baden-Württemberg": 0.05,
  Bremen: 0.05,
  Niedersachsen: 0.05,
  "Rheinland-Pfalz": 0.05,
  "Sachsen-Anhalt": 0.05,
  Sachsen: 0.055,
  Berlin: 0.06,
  Hessen: 0.06,
  "Mecklenburg-Vorpommern": 0.06,
  Brandenburg: 0.065,
  "Nordrhein-Westfalen": 0.065,
  Saarland: 0.065,
  "Schleswig-Holstein": 0.065,
  Thüringen: 0.065,
};

/**
 * Ermittelt den Grunderwerbssteuersatz anhand des Bundeslands.
 * Normalisiert dabei Google-API Langformen und englische Namen.
 *
 * @param {string} state - Bundesland (z.B. aus Google_Region oder State)
 * @param {number} fallback - Fallback-Wert wenn Bundesland unbekannt (Default: 0.06)
 * @returns {number} Grunderwerbssteuersatz (z.B. 0.065 für NRW)
 */
export const getLandTransferTax = (state, fallback = 0.06) => {
  if (!state) return fallback;
  const normalized = STATE_NAME_ALIASES[state.trim()] || state.trim();
  return GRUNDERWERBSSTEUER_BY_STATE[normalized] ?? fallback;
};

/**
 * Resolve equity ratio based on user settings (EUR absolute or percentage mode).
 * When mode is "eur", the ratio is derived from EquityAbsolute / grossValue.
 * When mode is "pct" (or default), EquityRatio is used directly.
 *
 * @param {Object} item - Property item with equity fields
 * @param {number} grossValue - Total acquisition cost (purchase price + Nebenkosten)
 * @returns {number} Equity ratio as decimal (e.g. 0.2 for 20%)
 */
export const resolveEquityRatio = (item, grossValue) => {
  if (item.EquityMode === "eur" && item.EquityAbsolute > 0 && grossValue > 0) {
    return Math.min(item.EquityAbsolute / grossValue, 1);
  }
  return item.EquityRatio || 0.2;
};

/**
 * Calculate Price per m² with color logic
 *
 * @param {Object} item - Property listing item
 * @param {number} item.PurchasePrice - Purchase price of the property
 * @param {number} item.Area - Area of the property in m²
 * @param {number} item.MaxPricePerSqm - Maximum price per m² from market data
 * @param {number} item.MinPricePerSqm - Minimum price per m² from market data
 * @returns {Object} Color information and text for display
 */
export const calculatePricePerSqmColor = (item) => {
  // Calculate price per m² if not already provided
  const pricePerSqm =
    item.PricePerSquareMeter ||
    (item.PurchasePrice && item.Area ? item.PurchasePrice / item.Area : null);

  if (!pricePerSqm || !item.MaxPricePerSqm || !item.MinPricePerSqm) {
    return {
      color: "#F0C854",
      text: "im Durchschnitt",
      showPercentage: false,
      pricePerSqm,
    };
  }

  // Calculate average from market data
  const avgPrice = (item.MaxPricePerSqm + item.MinPricePerSqm) / 2;
  const priceDiffRawPercentage = ((pricePerSqm - avgPrice) / avgPrice) * 100;

  if (priceDiffRawPercentage < -5) {
    // More than 5% cheaper (good)
    return {
      color: "#16a398",
      text: "unter dem Durchschnitt",
      showPercentage: true,
      percentage: Math.abs(priceDiffRawPercentage),
      pricePerSqm,
    };
  } else if (priceDiffRawPercentage > 5) {
    // More than 5% more expensive (bad)
    return {
      color: "#ef6e6e",
      text: "über dem Durchschnitt",
      showPercentage: true,
      percentage: Math.abs(priceDiffRawPercentage),
      pricePerSqm,
    };
  } else {
    // In between (neutral)
    return {
      color: "#F0C854",
      text: "im Durchschnitt",
      showPercentage: false,
      pricePerSqm,
    };
  }
};

/**
 * Calculate Cash Flow
 *
 * Formula (Updated):
 * - grossRent = avgRent * wohnflaeche
 * - ancillaryCosts = (wohnflaeche * reservesTotal) * sinkingCosts
 * - annualNetRent = (grossRent - ancillaryCosts) * 12
 * - lendingValue = (PurchasePrice + PurchasePrice * 0.106) - (PurchasePrice * EquityRatio)
 * - financingInstallment = (lendingValue * interestRate) / 12
 * - monthlyRevenue = (annualNetRent / 12) - financingInstallment
 *
 * @param {Object} item - Property listing item
 * @param {number} item.PurchasePrice - Purchase price of the property
 * @param {number} item.Area - Area of the property in m²
 * @param {number} item.RentPerSqm - Rent per m² from market data (durchschnittsmiete)
 * @param {number} item.EquityRatio - Equity ratio (default 0.2 = 20%)
 * @param {number} item.InterestRate - Interest rate (default 0.035 = 3.5%)
 * @returns {number} Monthly cash flow
 */
export const calculateCashflow = (item) => {
  const purchasePrice = item.PurchasePrice || 0;
  const wohnflaeche = item.Area || 0;

  // Use market data rent per sqm if available, otherwise use default
  const avgRent = item.RentPerSqm || item.durchschnittsmiete || 15;

  // Use user settings if available, otherwise use defaults.
  // Priority: User-Settings (item.*) > System-Default
  const interestRate = item.InterestRate || 0.035; // Default 3.5%
  const totalAcquisitionCosts =
    (item.AgentCosts ?? 0.0357) +
    (item.NotaryCosts ?? 0.02) +
    (item.LandTransferTax ?? 0.065);

  // Global default parameters (hardcoded)
  const reservesTotal = 2.5;
  const sinkingCosts = 0.4;

  // Calculate gross rent (monthly)
  const grossRent = avgRent * wohnflaeche;

  // Calculate ancillary costs
  const ancillaryCosts = wohnflaeche * reservesTotal * sinkingCosts;

  // Calculate annual net rent
  const annualNetRent = (grossRent - ancillaryCosts) * 12;

  // Calculate lending value (loan amount)
  const grossValue = purchasePrice + purchasePrice * totalAcquisitionCosts;

  // Resolve equity ratio based on mode (EUR absolute or percentage)
  const equityRatio = resolveEquityRatio(item, grossValue);

  const lendingValue = grossValue * (1 - equityRatio);

  // Calculate monthly financing installment
  const financingInstallment = (lendingValue * interestRate) / 12;

  // Calculate monthly revenue (cash flow)
  const monthlyRevenue = annualNetRent / 12 - financingInstallment;

  // TODO Phase 4: Diese Berechnung soll aus /api/analyzeExpose kommen
  return monthlyRevenue;
};

/**
 * Calculate Cash Flow color based on value
 *
 * @param {number} cashflow - Monthly cash flow value
 * @returns {string} Color code for display
 */
export const calculateCashflowColor = (cashflow) => {
  if (cashflow < 0) {
    return "#ef6e6e"; // Red for negative
  } else if (cashflow < 100) {
    return "#667085"; // Gray for low/neutral
  } else {
    return "#16a398"; // Green for positive
  }
};

/**
 * Calculate Return on Equity (EK-Rendite)
 *
 * Formula (Updated):
 * - grossRent = avgRent * wohnflaeche
 * - ancillaryCosts = (wohnflaeche * reservesTotal) * sinkingCosts
 * - annualNetRent = (grossRent - ancillaryCosts) * 12
 * - equityTotal = (pricePerSqm * wohnflaeche) * equity
 * - financingInstallment = (lendingValue * interestRate) / 12
 * - annualRevenue = annualNetRent - (financingInstallment * 12)
 * - rendite = (annualRevenue / equityTotal) * 100
 *
 * @param {Object} item - Property listing item
 * @param {number} item.PurchasePrice - Purchase price of the property
 * @param {number} item.Area - Area of the property in m²
 * @param {number} item.RentPerSqm - Rent per m² from market data
 * @param {number} item.EquityRatio - Equity ratio (default 0.2 = 20%)
 * @param {number} item.InterestRate - Interest rate (default 0.035 = 3.5%)
 * @returns {number} Return on equity as percentage
 */
export const calculateEKRendite = (item) => {
  const purchasePrice = item.PurchasePrice || 0;
  const wohnflaeche = item.Area || 0;

  // Use market data rent per sqm if available, otherwise use default
  const avgRent = item.RentPerSqm || item.durchschnittsmiete || 15;

  // Use user settings if available, otherwise use defaults.
  // Priority: User-Settings (item.*) > System-Default
  const interestRate = item.InterestRate || 0.035; // Default 3.5%
  const totalAcquisitionCosts =
    (item.AgentCosts ?? 0.0357) +
    (item.NotaryCosts ?? 0.02) +
    (item.LandTransferTax ?? 0.065);

  // Global default parameters (hardcoded)
  const reservesTotal = 2.5;
  const sinkingCosts = 0.4;

  // Calculate price per sqm
  const pricePerSqm = purchasePrice / wohnflaeche;

  // Calculate gross rent (monthly)
  const grossRent = avgRent * wohnflaeche;

  // Calculate ancillary costs
  const ancillaryCosts = wohnflaeche * reservesTotal * sinkingCosts;

  // Calculate annual net rent
  const annualNetRent = (grossRent - ancillaryCosts) * 12;

  // Calculate lending value (loan amount)
  const grossValue = purchasePrice + purchasePrice * totalAcquisitionCosts;

  // Resolve equity ratio based on mode (EUR absolute or percentage)
  const equity = resolveEquityRatio(item, grossValue);

  const lendingValue = grossValue - grossValue * equity;

  // Calculate equity total
  const equityTotal = grossValue * equity;

  // Calculate annual financing installment
  const financingInstallment = lendingValue * interestRate;

  // Calculate annual revenue
  const annualRevenue = annualNetRent - financingInstallment;

  // Calculate return on equity
  if (equityTotal === 0) return 0;

  const rendite = (annualRevenue / equityTotal) * 100;

  // TODO Phase 4: Diese Berechnung soll aus /api/analyzeExpose kommen
  return rendite;
};

/**
 * Calculate Return on Equity color based on value
 *
 * @param {number} rendite - Return on equity percentage
 * @returns {string} Color code for display
 */
export const calculateEKRenditeColor = (rendite) => {
  if (rendite > 6) {
    return "#1DB3B3"; // Cyan for values over 6%
  } else if (rendite < 4) {
    return "#E83838"; // Red for values under 4%
  } else {
    return "#F0C045"; // Yellow for values between 4-6%
  }
};

/**
 * Enrich property data with market data
 *
 * @param {Object} property - Property listing item
 * @param {Object} marketData - Market data from API response
 * @returns {Object} Property with enriched market data
 *
 */

export const enrichPropertyWithMarketData = (property, marketData) => {
  if (!marketData) {
    return {
      ...property,
      MaxPricePerSqm: null,
      MinPricePerSqm: null,
      AvgPricePerSqm: null,
      RentPerSqm: null,
      MaxRentPerSqm: null,
      MinRentPerSqm: null,
      MarketDataSource: null,
    };
  }

  // Handle both nested and flat market data structures
  const maxPrice =
    marketData?.kaufpreise?.max_kaufpreis_pro_m2 ||
    marketData.max_kaufpreis_pro_m2;
  const minPrice =
    marketData.kaufpreise?.min_kaufpreis_pro_m2 ||
    marketData.min_kaufpreis_pro_m2;
  const avgPrice =
    marketData.kaufpreise?.durchschnittskaufpreis ||
    marketData.durchschnittskaufpreis;
  const avgRent =
    marketData.mieten?.durchschnittsmiete || marketData.durchschnittsmiete;
  const maxRent =
    marketData.mieten?.max_miete_pro_m2 || marketData.max_miete_pro_m2;
  const minRent =
    marketData.mieten?.min_miete_pro_m2 || marketData.min_miete_pro_m2;

  return {
    ...property,
    // Add market data fields
    MaxPricePerSqm: maxPrice,
    MinPricePerSqm: minPrice,
    AvgPricePerSqm: avgPrice,
    RentPerSqm: avgRent,
    MaxRentPerSqm: maxRent,
    MinRentPerSqm: minRent,
    MarketDataSource: marketData.quelle,
  };
};
